import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, describeApiError, getCsrfToken } from '../lib/api';
import { QASkeleton } from './QASkeleton';
import { QAEmpty } from './QAEmpty';
import { formatDateAU } from '../../../shared/format';
import { DOC_CATEGORIES, DOC_CATEGORY_LABEL, MAX_DOC_BYTES, RETENTION_YEARS, contentTypeFor, type DocCategory } from '../../../shared/documents';

interface Doc {
  id: string;
  category: DocCategory;
  title: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  documentDate: string | null;
  source: string;
  notes: string | null;
  createdAt: string;
  uploadedBy: string | null;
}
interface VaultResponse { data: Doc[]; retention: { relationshipEndedOn: string | null; keepUntil: string | null } }

const kb = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.msg,.eml,.jpg,.jpeg,.png,.heic';

/**
 * Documents vault: every file on the record, by category. Removing a document takes it off
 * the record but the stored file is kept for the retention period.
 */
export function VaultTab({ clientId, canEdit }: { clientId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const key = ['client', clientId, 'documents'];
  const q = useQuery({ queryKey: key, queryFn: () => api<VaultResponse>(`/api/clients/${clientId}/documents`) });
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState<DocCategory>('other');
  const [title, setTitle] = useState('');
  const [docDate, setDocDate] = useState('');
  const [filter, setFilter] = useState<DocCategory | 'all'>('all');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ id: string; title: string; category: DocCategory; documentDate: string } | null>(null);

  const rows = useMemo(() => q.data?.data ?? [], [q.data]);
  const counts = useMemo(() => {
    const m = new Map<DocCategory, number>();
    for (const r of rows) m.set(r.category, (m.get(r.category) ?? 0) + 1);
    return m;
  }, [rows]);
  const shown = filter === 'all' ? rows : rows.filter((r) => r.category === filter);

  if (q.isLoading) return <QASkeleton variant="table" rows={6} />;
  if (q.isError || !q.data) return <div className="qa-badge qa-badge--out">Unable to load documents</div>;
  const ret = q.data.retention;

  function pick(f: File | null) {
    setMsg(null);
    if (f && !contentTypeFor(f.name)) { setMsg('That file type is not accepted.'); setFile(null); return; }
    if (f && f.size > MAX_DOC_BYTES) { setMsg(`Files up to ${MAX_DOC_BYTES / 1024 / 1024} MB.`); setFile(null); return; }
    setFile(f);
  }

  async function uploadFile() {
    if (!file) return;
    setBusy(true); setMsg(null);
    const fd = new FormData();
    fd.append('category', category);
    if (title.trim()) fd.append('title', title.trim());
    if (docDate) fd.append('documentDate', docDate);
    fd.append('file', file);
    try {
      // FormData: skip the JSON wrapper; the CSRF token travels in the header.
      const r = await fetch(`/api/clients/${clientId}/documents`, {
        method: 'POST', body: fd, credentials: 'include', headers: { 'x-csrf-token': getCsrfToken() || '' },
      });
      if (!r.ok) {
        const b = await r.json().catch(() => null) as { message?: string } | null;
        throw new Error(b?.message ?? 'Upload failed');
      }
      setMsg(`Saved ${file.name}.`);
      setFile(null); setTitle(''); setDocDate(''); setCategory('other');
      if (fileRef.current) fileRef.current.value = '';
      void qc.invalidateQueries({ queryKey: key });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Upload failed');
    } finally { setBusy(false); }
  }

  async function saveEdit() {
    if (!editing) return;
    setBusy(true); setMsg(null);
    try {
      await api(`/api/clients/${clientId}/documents/${editing.id}`, {
        method: 'PATCH', body: JSON.stringify({ title: editing.title, category: editing.category, documentDate: editing.documentDate || null }),
      });
      setEditing(null);
      void qc.invalidateQueries({ queryKey: key });
    } catch (e) { setMsg(describeApiError(e)); } finally { setBusy(false); }
  }

  async function remove(d: Doc) {
    if (!window.confirm(`Remove "${d.title}" from the record? The stored file is kept for the retention period.`)) return;
    setBusy(true); setMsg(null);
    try {
      await api(`/api/clients/${clientId}/documents/${d.id}`, { method: 'DELETE' });
      setMsg(`Removed ${d.title}.`);
      void qc.invalidateQueries({ queryKey: key });
    } catch (e) { setMsg(describeApiError(e)); } finally { setBusy(false); }
  }

  return (
    <div className="space-y-6" data-testid="vault-tab">
      {canEdit && (
        <section className="qa-card p-5" aria-label="Add a document">
          <p className="qa-eyebrow mb-4">Add a document</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs items-end">
            <label className="lg:col-span-1">File
              <input ref={fileRef} type="file" accept={ACCEPT} className="block w-full mt-1 text-sm" aria-label="File"
                onChange={(e) => pick(e.target.files?.[0] ?? null)} />
            </label>
            <label>Category
              <select className="qa-input block w-full mt-1 text-sm" aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value as DocCategory)}>
                {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{DOC_CATEGORY_LABEL[c]}</option>)}
              </select>
            </label>
            <label>Title (optional)<input className="qa-input block w-full mt-1 text-sm" aria-label="Title" placeholder="From the file name" value={title} onChange={(e) => setTitle(e.target.value)} /></label>
            <label>Document date<input type="date" className="qa-input block w-full mt-1 text-sm" aria-label="Document date" value={docDate} onChange={(e) => setDocDate(e.target.value)} /></label>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button type="button" className="btn-primary text-sm" disabled={busy || !file} onClick={uploadFile}>{busy ? 'Saving...' : 'Save to vault'}</button>
            <span className="text-xs text-ink-mute">PDF, Word, Excel, email, images or text, up to {MAX_DOC_BYTES / 1024 / 1024} MB.</span>
          </div>
        </section>
      )}

      <div role="status" aria-live="polite" className={msg ? 'qa-attention-banner text-sm' : 'sr-only'}>{msg}</div>

      <section className="qa-card p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
          <p className="qa-eyebrow">Documents · {rows.length}</p>
          <label className="text-xs flex items-center gap-2">Show
            <select className="qa-input text-sm" aria-label="Filter by category" value={filter} onChange={(e) => setFilter(e.target.value as DocCategory | 'all')}>
              <option value="all">All categories</option>
              {DOC_CATEGORIES.filter((c) => counts.get(c)).map((c) => <option key={c} value={c}>{DOC_CATEGORY_LABEL[c]} ({counts.get(c)})</option>)}
            </select>
          </label>
        </div>
        {shown.length === 0 ? (
          <QAEmpty inline title="No documents yet.">{canEdit ? 'Add the fact find, SoA, consents and statements as they come in.' : undefined}</QAEmpty>
        ) : (
          <div className="overflow-x-auto" role="region" aria-label="Documents" tabIndex={0}>
            <table className="qa-table w-full">
              <thead><tr><th>Title</th><th>Category</th><th>Date</th><th className="text-right">Size</th><th>Added</th><th /></tr></thead>
              <tbody>
                {shown.map((d) => editing?.id === d.id ? (
                  <tr key={d.id}>
                    <td><input className="qa-input w-full text-sm" aria-label="Edit title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></td>
                    <td>
                      <select className="qa-input text-sm" aria-label="Edit category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value as DocCategory })}>
                        {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{DOC_CATEGORY_LABEL[c]}</option>)}
                      </select>
                    </td>
                    <td><input type="date" className="qa-input text-sm" aria-label="Edit document date" value={editing.documentDate} onChange={(e) => setEditing({ ...editing, documentDate: e.target.value })} /></td>
                    <td /><td />
                    <td className="text-right whitespace-nowrap">
                      <button type="button" className="text-xs text-rust hover:underline mr-3" disabled={busy || !editing.title.trim()} onClick={saveEdit}>Save</button>
                      <button type="button" className="text-xs text-ink-mute hover:underline" onClick={() => setEditing(null)}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={d.id}>
                    <td>
                      <a className="text-ink hover:text-rust font-medium" href={`/api/clients/${clientId}/documents/${d.id}/download${/\.(pdf|png|jpe?g)$/i.test(d.fileName) ? '?inline=1' : ''}`} target="_blank" rel="noopener">{d.title}</a>
                      <div className="text-[11px] text-ink-mute qa-mono">{d.fileName}</div>
                    </td>
                    <td>{DOC_CATEGORY_LABEL[d.category] ?? d.category}</td>
                    <td className="qa-mono text-xs">{d.documentDate ? formatDateAU(d.documentDate) : ''}</td>
                    <td className="num">{kb(d.sizeBytes)}</td>
                    <td className="text-xs text-ink-mute">{formatDateAU(d.createdAt)}{d.uploadedBy ? ` · ${d.uploadedBy}` : ''}</td>
                    <td className="text-right whitespace-nowrap">
                      <a className="text-xs text-rust hover:underline mr-3" href={`/api/clients/${clientId}/documents/${d.id}/download`} aria-label={`Download ${d.title}`}>Download</a>
                      {canEdit && <button type="button" className="text-xs text-rust hover:underline mr-3" disabled={busy} aria-label={`Edit ${d.title}`}
                        onClick={() => setEditing({ id: d.id, title: d.title, category: d.category, documentDate: d.documentDate ?? '' })}>Edit</button>}
                      {canEdit && <button type="button" className="text-xs text-ink-mute hover:underline" disabled={busy} aria-label={`Remove ${d.title}`} onClick={() => remove(d)}>Remove</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-ink-mute mt-3">
          {ret.keepUntil
            ? `Relationship ended ${formatDateAU(ret.relationshipEndedOn)}. Records are kept until ${formatDateAU(ret.keepUntil)}.`
            : `Records are kept while the client is active and for ${RETENTION_YEARS} years after the relationship ends. Removing a document takes it off the record; the stored file is kept.`}
        </p>
      </section>
    </div>
  );
}
